<script setup lang="ts">
import { computed, onMounted, nextTick } from 'vue';
import SearchFilter from '@/components/videos/SearchFilter.vue';
import SubjectList from '@/components/videos/SubjectList.vue';
import SubjectVideoList from '@/components/videos/SubjectVideoList.vue';
import FeaturedVideoList from '@/components/videos/FeaturedVideoList.vue';
import FeaturedArticlesList from '@/components/articles/FeaturedArticlesList.vue';
import { useVideos } from '@ilteducation/videos';
import { storeToRefs } from 'pinia';
import { useLocale } from '@/plugins/translation';
import { useHead } from '@unhead/vue';
import capitalize from 'lodash/capitalize';
import config from '@/config/ConfigProvider';

const { t, l } = useLocale();
const videoStore = useVideos();
const { searchMode } = storeToRefs(videoStore);

const isSearching = computed(() => searchMode.value);

const countryCode = config.value('countryCode') as string;
const isSE = computed(() => countryCode === 'se');

const serviceName = config.value('serviceName');
useHead({ title: computed(() => t(l.videos.page_title, { service: capitalize(serviceName) })) });

onMounted(async () => {
  const scrollTop = localStorage.getItem('videoScrollTop');

  if (scrollTop) {
    nextTick(() => {
      window.scrollTo({ top: parseInt(scrollTop, 10), behavior: 'smooth' });
      localStorage.removeItem('videoScrollTop');
    });
  }
});
</script>

<template>
  <v-container class="pt-2">
    <v-col class="text-center pb-0">
      <h1 class="text-secondary">{{ t(l.videos.title) }}</h1>
      <p class="d-sr-only">{{ t(l.videos.page_description) }}</p>
    </v-col>
    <v-row>
      <v-col class="custom-container mx-auto mb-9">
        <SearchFilter />
      </v-col>
    </v-row>
    <template v-if="!isSearching">
      <v-row v-if="!isSE" class="d-none d-sm-block">
        <v-col class="custom-container mx-auto mb-12 py-0">
          <h2 class="d-sr-only">{{ t(l.label_list_of_subjects) }}</h2>
          <SubjectList size="large" />
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-container class="custom-container py-0">
          <!-- transition disabled: the default fade renders the text at unreadable contrast while it runs -->
          <v-lazy :options="{ threshold: 0.1 }" :transition="false">
            <FeaturedVideoList />
          </v-lazy>
        </v-container>
      </v-row>
      <template v-if="isSE">
        <v-row class="mt-0">
          <v-container class="custom-container py-0">
            <v-lazy :options="{ threshold: 0.1 }" :transition="false">
              <FeaturedArticlesList />
            </v-lazy>
          </v-container>
        </v-row>
        <v-row class="d-none d-sm-block">
          <v-col class="custom-container mx-auto mb-12 py-0">
            <h2 class="text-secondary mt-0 mb-6 subjects-heading">{{ t(l.label_list_of_subjects) }}</h2>
            <SubjectList size="large" />
          </v-col>
        </v-row>
      </template>
      <v-row>
        <SubjectVideoList />
      </v-row>
      <v-row class="d-block d-sm-none">
        <v-container class="custom-container pb-6 pb-md-0">
          <v-row>
            <v-col>
              <h2 :class="isSE ? 'text-secondary mt-0 mb-6 subjects-heading' : 'd-sr-only'">
                {{ t(l.label_list_of_subjects) }}
              </h2>
              <SubjectList size="small" />
            </v-col>
          </v-row>
        </v-container>
      </v-row>
    </template>
  </v-container>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';
.custom-container {
  max-width: 78.125rem !important;
}

.subjects-heading {
  font-size: 1.125rem;

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    font-size: 1.375rem;
  }
}
</style>
